import React, { createContext, useContext, useState, useCallback } from 'react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContext';

// Create the context
const SubscriptionContext = createContext();

// Create a provider component
export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch subscription plans
  const fetchSubscriptionPlans = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.getSubscriptionPlans();
      setSubscriptionPlans(response.plans || []);
      setError(null);
      return response.plans;
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
      setError('Failed to fetch subscription plans');
      addToast({
        type: 'error',
        message: 'Failed to fetch subscription plans'
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  // Fetch user subscriptions
  const fetchUserSubscriptions = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.getUserSubscriptions();
      setUserSubscriptions(response.subscriptions || []);
      setError(null);
      return response.subscriptions;
    } catch (err) {
      console.error('Error fetching user subscriptions:', err);
      setError('Failed to fetch your subscriptions');
      addToast({
        type: 'error',
        message: 'Failed to fetch your subscriptions'
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  // Subscribe to a plan
  const subscribeToPlan = useCallback(async (planId) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.subscribeToPlan(planId);
      // Refresh user subscriptions after subscribing
      await fetchUserSubscriptions();
      setError(null);
      addToast({
        type: 'success',
        message: 'Successfully subscribed to plan'
      });
      return response;
    } catch (err) {
      console.error('Error subscribing to plan:', err);
      setError('Failed to subscribe to plan');
      addToast({
        type: 'error',
        message: err.message || 'Failed to subscribe to plan'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, fetchUserSubscriptions, addToast]);

  // Cancel subscription
  const cancelSubscription = useCallback(async (subscriptionId) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.cancelSubscription(subscriptionId);
      // Refresh user subscriptions after cancelling
      await fetchUserSubscriptions();
      setError(null);
      addToast({
        type: 'success',
        message: 'Successfully cancelled subscription'
      });
      return response;
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription');
      addToast({
        type: 'error',
        message: err.message || 'Failed to cancel subscription'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, fetchUserSubscriptions, addToast]);

  // Fetch credit balance
  const fetchCreditBalance = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.getCreditBalance();
      setCreditBalance(response.balance || 0);
      setError(null);
      return response.balance;
    } catch (err) {
      console.error('Error fetching credit balance:', err);
      setError('Failed to fetch credit balance');
      addToast({
        type: 'error',
        message: 'Failed to fetch credit balance'
      });
      return 0;
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  // Fetch credit transactions
  const fetchCreditTransactions = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.getCreditTransactions();
      setCreditTransactions(response.transactions || []);
      setError(null);
      return response.transactions;
    } catch (err) {
      console.error('Error fetching credit transactions:', err);
      setError('Failed to fetch credit transactions');
      // Don't show a toast for this as it's not critical
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  // Purchase credits
  const purchaseCredits = useCallback(async (amount, credits) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiService.purchaseCredits(amount, credits);
      // Refresh credit balance and transactions after purchase
      await Promise.all([
        fetchCreditBalance(),
        fetchCreditTransactions()
      ]);
      setError(null);
      addToast({
        type: 'success',
        message: `Successfully purchased ${credits} credits`
      });
      return response;
    } catch (err) {
      console.error('Error purchasing credits:', err);
      setError('Failed to purchase credits');
      addToast({
        type: 'error',
        message: err.message || 'Failed to purchase credits'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, fetchCreditBalance, fetchCreditTransactions, addToast]);

  // Check if user has an active subscription
  const hasActiveSubscription = useCallback(() => {
    return userSubscriptions.some(sub => sub.status === 'active');
  }, [userSubscriptions]);

  // Get the highest tier active subscription
  const getActiveSubscription = useCallback(() => {
    const activeSubscriptions = userSubscriptions.filter(sub => sub.status === 'active');
    if (activeSubscriptions.length === 0) return null;
    
    // Sort by price (highest first) and return the first one
    return activeSubscriptions.sort((a, b) => b.plan.price - a.plan.price)[0];
  }, [userSubscriptions]);

  // Value to be provided by the context
  const value = {
    subscriptionPlans,
    userSubscriptions,
    creditBalance,
    creditTransactions,
    loading,
    error,
    fetchSubscriptionPlans,
    fetchUserSubscriptions,
    subscribeToPlan,
    cancelSubscription,
    fetchCreditBalance,
    fetchCreditTransactions,
    purchaseCredits,
    hasActiveSubscription,
    getActiveSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
